import { Card } from '@/components/ui/card';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { CheckCircle2, XCircle, AlertTriangle, TrendingUp, Info, GitBranch } from 'lucide-react';
import { useFlexESimulation } from '@/hooks/useFlexESimulation';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface FlexESummaryCardProps {
  simId: string;
}

export default function FlexESummaryCard({ simId }: FlexESummaryCardProps) {
  const { simulation, isLoading } = useFlexESimulation(simId);

  if (isLoading || !simulation) {
    return (
      <Card className="p-6">
        <Skeleton className="h-32 w-full" />
      </Card>
    );
  }

  const getRecommendation = (successRate: number) => {
    if (successRate >= 95) {
      return {
        icon: <CheckCircle2 className="h-4 w-4" />,
        color: 'green',
        title: 'Batterij is voldoende',
        message: 'De huidige batterijconfiguratie voldoet uitstekend aan de contracteisen.'
      };
    } else if (successRate >= 80) {
      return {
        icon: <AlertTriangle className="h-4 w-4" />,
        color: 'yellow',
        title: 'Batterij bijna voldoende',
        message: 'Overweeg een capaciteitsverhoging van 10-15% voor optimale betrouwbaarheid.'
      };
    } else {
      return {
        icon: <XCircle className="h-4 w-4" />,
        color: 'red',
        title: 'Batterij onvoldoende',
        message: 'Verhoog de batterijcapaciteit of heronderhandel de contractlimiet.'
      };
    }
  };

  const recommendation = getRecommendation(simulation.success_rate || 0);

  return (
    <TooltipProvider>
      <div id="flexe-chart-summary" className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-2xl">3️⃣</span>
            <h2 className="text-xl font-semibold">Resultaten</h2>
          </div>
          {simulation.name && (
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Simulatie</p>
              <p className="font-semibold">{simulation.name}</p>
            </div>
          )}
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="flex items-center justify-between mb-1">
              <p className="text-sm text-muted-foreground">Success Rate</p>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-[280px]">
                  <p className="text-xs">Percentage dagen waarop de batterij zowel het vermogen als de energie kon leveren om onder de contractlimiet te blijven.</p>
                </TooltipContent>
              </Tooltip>
            </div>
            <p className="text-3xl font-bold text-green-600 dark:text-green-400">
              {simulation.success_rate?.toFixed(1) || 0}%
            </p>
          </Card>

          <Card className="p-4">
            <div className="flex items-center justify-between mb-1">
              <p className="text-sm text-muted-foreground">Geslaagde Dagen</p>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-[280px]">
                  <p className="text-xs">Aantal dagen waarop de batterij voldoende capaciteit en vermogen had om alle overschrijdingen op te vangen.</p>
                </TooltipContent>
              </Tooltip>
            </div>
            <p className="text-3xl font-bold">
              {simulation.passed_days || 0}
            </p>
            <p className="text-xs text-muted-foreground">
              van {simulation.total_days || 0}
            </p>
          </Card>

          <Card className="p-4">
            <div className="flex items-center justify-between mb-1">
              <p className="text-sm text-muted-foreground">Gefaalde Dagen</p>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-[280px]">
                  <p className="text-xs">Aantal dagen waarop de batterij tekort kwam in vermogen of energiecapaciteit.</p>
                </TooltipContent>
              </Tooltip>
            </div>
            <p className="text-3xl font-bold text-red-600 dark:text-red-400">
              {simulation.failed_days || 0}
            </p>
          </Card>

          <Card className="p-4">
            <div className="flex items-center justify-between mb-1">
              <p className="text-sm text-muted-foreground">Slechtste Dag</p>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-[280px]">
                  <p className="text-xs">De dag met de hoogste energiebehoefte. Kritisch voor het bepalen van de benodigde batterijcapaciteit.</p>
                </TooltipContent>
              </Tooltip>
            </div>
            <p className="text-xl font-bold">
              {simulation.worst_day ? new Date(simulation.worst_day).toLocaleDateString('nl-NL') : '-'}
            </p>
            <p className="text-sm text-muted-foreground">
              {simulation.worst_deficit_kwh?.toFixed(1) || 0} kWh tekort
            </p>
          </Card>
        </div>

      {/* Recommendation Alert */}
      <Alert className={cn(
        "border-2",
        recommendation.color === "green" && "border-green-500 bg-green-50 dark:bg-green-950",
        recommendation.color === "yellow" && "border-yellow-500 bg-yellow-50 dark:bg-yellow-950",
        recommendation.color === "red" && "border-red-500 bg-red-50 dark:bg-red-950"
      )}>
        {recommendation.icon}
        <AlertTitle>{recommendation.title}</AlertTitle>
        <AlertDescription>{recommendation.message}</AlertDescription>
      </Alert>

      {/* Scenario Impact Comparison */}
      {simulation.scenario_applied && simulation.baseline_success_rate != null && (
        <Card className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950 dark:to-indigo-950 border-blue-200 dark:border-blue-800">
          <div className="flex items-center gap-2 mb-4">
            <GitBranch className="h-5 w-5 text-blue-600" />
            <h3 className="text-lg font-semibold">Scenario Impact</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            {/* Header Row - Hidden on mobile, shown as labels on desktop */}
            <div className="hidden md:block text-xs text-muted-foreground font-medium">Metriek</div>
            <div className="hidden md:block text-xs text-muted-foreground font-medium text-center">Baseline</div>
            <div className="hidden md:block text-xs text-muted-foreground font-medium text-center">Met Scenario</div>
            
            {/* Success Rate */}
            <div className="font-medium md:col-span-1">Success Rate</div>
            <div className="text-center px-3 py-2 bg-white dark:bg-gray-900 rounded-lg">
              <p className="text-sm text-muted-foreground md:hidden mb-1">Baseline</p>
              <p className="text-xl font-bold text-red-600">
                {simulation.baseline_success_rate?.toFixed(1)}%
              </p>
            </div>
            <div className="text-center px-3 py-2 bg-white dark:bg-gray-900 rounded-lg">
              <p className="text-sm text-muted-foreground md:hidden mb-1">Met Scenario</p>
              <p className="text-xl font-bold text-green-600">
                {simulation.success_rate?.toFixed(1)}%
              </p>
              <p className="text-xs text-green-600 font-medium">
                +{(simulation.success_rate - simulation.baseline_success_rate).toFixed(1)}%
              </p>
            </div>
            
            {/* Passed Days */}
            <div className="font-medium md:col-span-1">Geslaagde Dagen</div>
            <div className="text-center px-3 py-2 bg-white dark:bg-gray-900 rounded-lg">
              <p className="text-sm text-muted-foreground md:hidden mb-1">Baseline</p>
              <p className="text-xl font-bold">{simulation.baseline_passed_days}</p>
            </div>
            <div className="text-center px-3 py-2 bg-white dark:bg-gray-900 rounded-lg">
              <p className="text-sm text-muted-foreground md:hidden mb-1">Met Scenario</p>
              <p className="text-xl font-bold">{simulation.passed_days}</p>
              <p className="text-xs text-green-600 font-medium">
                +{simulation.passed_days - (simulation.baseline_passed_days || 0)} dagen
              </p>
            </div>
            
            {/* Failed Days */}
            <div className="font-medium md:col-span-1">Gefaalde Dagen</div>
            <div className="text-center px-3 py-2 bg-white dark:bg-gray-900 rounded-lg">
              <p className="text-sm text-muted-foreground md:hidden mb-1">Baseline</p>
              <p className="text-xl font-bold text-red-600">
                {simulation.baseline_failed_days}
              </p>
            </div>
            <div className="text-center px-3 py-2 bg-white dark:bg-gray-900 rounded-lg">
              <p className="text-sm text-muted-foreground md:hidden mb-1">Met Scenario</p>
              <p className="text-xl font-bold">
                {simulation.failed_days}
              </p>
              <p className="text-xs text-green-600 font-medium">
                -{(simulation.baseline_failed_days || 0) - simulation.failed_days} dagen
              </p>
            </div>
          </div>
          
          {/* Scenario Details */}
          {simulation.scenario_summary && (
            <Alert className="bg-white/50 dark:bg-gray-900/50 border-blue-300 dark:border-blue-700">
              <AlertTitle className="text-sm font-semibold">Scenario Details</AlertTitle>
              <AlertDescription className="text-sm">
                {(simulation.scenario_summary as any).machine_count} machines toegevoegd 
                ({(simulation.scenario_summary as any).new_count} nieuw, {(simulation.scenario_summary as any).replacement_count} vervangen)
                <br />
                Totaal effect: <strong className="text-blue-600 dark:text-blue-400">
                  {(simulation.scenario_summary as any).total_delta_kw > 0 ? '+' : ''}
                  {((simulation.scenario_summary as any).total_delta_kw)?.toFixed(1)} kW
                </strong> tijdens {(simulation.scenario_summary as any).window?.start} - {(simulation.scenario_summary as any).window?.end}
              </AlertDescription>
            </Alert>
          )}
        </Card>
      )}

      {/* Battery Config Summary */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-medium">Batterijconfiguratie</h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
          <div>
            <p className="text-muted-foreground">Vermogen</p>
            <p className="font-medium">{simulation.batt_kw} kW</p>
          </div>
          <div>
            <p className="text-muted-foreground">Capaciteit</p>
            <p className="font-medium">{simulation.batt_kwh?.toFixed(1)} kWh</p>
          </div>
          <div>
            <p className="text-muted-foreground">C-waarde</p>
            <p className="font-medium">{simulation.c_value}</p>
          </div>
          <div>
            <div className="flex items-center gap-1">
              <p className="text-muted-foreground">Contractlimiet</p>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-[280px]">
                  <p className="text-xs">
                    Maximaal toegestaan vermogen tijdens piekuren. 
                    De batterij moet het verschil tussen werkelijk verbruik en deze limiet compenseren.
                  </p>
                </TooltipContent>
              </Tooltip>
            </div>
            <p className="font-medium">{simulation.cap_limited_kw} kW</p>
          </div>
          <div>
            <div className="flex items-center gap-1">
              <p className="text-muted-foreground">Piekuren Window</p>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-[280px]">
                  <p className="text-xs">
                    Tijdvenster waarin de contractlimiet geldt. 
                    Buiten deze uren geldt het normale contractvermogen.
                  </p>
                </TooltipContent>
              </Tooltip>
            </div>
            <p className="font-medium">{simulation.window_start} - {simulation.window_end}</p>
          </div>
        </div>
        </Card>
      </div>
    </TooltipProvider>
  );
}
